import { useEffect, useState } from "react";
import { useModals } from "../common/ModalsProvider.js";
import { useT } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";

/*
 * CEO 2026-11-01 — a janela de envio da fatura saiu da tela Faturas para ser
 * aberta também pelo rascunho ("Émettre et envoyer").
 */

export type EmailInvoice = {
  id: number;
  invoiceNumber: string;
  mode?: "invoice" | "reminder";
  issuedAt?: string | null;
  totalTtc?: string | null;
  totalHt?: string | null;
  customerName?: string | null;
  /** F-saldo-unico — para cobrar o reste dû (fatura paga em parte / com avoir). */
  receivedHt?: string | number | null;
  creditNotesHt?: string | number | null;
};

/** Reste dû TTC (null = sem valor conhecido). */
function resteTtc(i: EmailInvoice): number | null {
  const ht = Number(i.totalHt ?? 0);
  if (!(ht > 0)) return null;
  const saldoHt = Math.max(0, ht - Number(i.receivedHt ?? 0) - Number(i.creditNotesHt ?? 0));
  const fator = i.totalTtc != null ? Number(i.totalTtc) / ht : 1;
  return Math.round(saldoHt * fator * 100) / 100;
}

const fmtEur = (v: string | number | null | undefined) =>
  `${Number(v ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDataFr = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR") : "—";

/**
 * F-relance-texto (CEO 2026-10-12): mensagem de cobrança pré-preenchida,
 * educada e formal, em francês (idioma dos e-mails ao cliente). O operador
 * pode editar antes de enviar.
 */
function textoRelanceFr(invs: EmailInvoice[]): string {
  const unidade = (i: EmailInvoice) => (i.totalTtc != null ? "TTC" : "HT");
  const cheio = (i: EmailInvoice) => Number(i.totalTtc ?? i.totalHt ?? 0);
  const reste = (i: EmailInvoice) => resteTtc(i) ?? cheio(i);
  const parcial = (i: EmailInvoice) => reste(i) < cheio(i) - 0.01;
  const valor = (i: EmailInvoice) =>
    parcial(i)
      ? `${fmtEur(reste(i))} ${unidade(i)} restant dus (sur ${fmtEur(cheio(i))} ${unidade(i)})`
      : `${fmtEur(cheio(i))} ${unidade(i)}`;
  const fecho =
    "Nous vous remercions de bien vouloir procéder au règlement dans les meilleurs délais, ou de nous communiquer la date et la référence du paiement s'il a déjà été effectué.\n\nSi votre règlement nous est parvenu entre-temps, nous vous prions de ne pas tenir compte de ce rappel.\n\nNous restons à votre disposition pour toute question et vous remercions de votre confiance.";
  if (invs.length === 1) {
    const i = invs[0]!;
    return parcial(i)
      ? `Sauf erreur ou omission de notre part, notre facture ${i.invoiceNumber} du ${fmtDataFr(i.issuedAt)}, d'un montant de ${fmtEur(cheio(i))} ${unidade(i)}, demeure partiellement impayée à ce jour : le solde restant dû s'élève à ${fmtEur(reste(i))} ${unidade(i)}.\n\n${fecho}`
      : `Sauf erreur ou omission de notre part, notre facture ${i.invoiceNumber} du ${fmtDataFr(i.issuedAt)}, d'un montant de ${valor(i)}, demeure impayée à ce jour.\n\n${fecho}`;
  }
  const linhas = invs
    .map((i) => `• ${i.invoiceNumber} du ${fmtDataFr(i.issuedAt)} — ${valor(i)}`)
    .join("\n");
  const total = invs.reduce((a, i) => a + reste(i), 0);
  const ttc = invs.every((i) => i.totalTtc != null) ? "TTC" : "HT";
  return `Sauf erreur ou omission de notre part, les factures suivantes demeurent impayées à ce jour :\n${linhas}\nSoit un solde restant dû de ${fmtEur(total)} ${ttc}.\n\n${fecho}`;
}

/**
 * F-invoice-email — Modal de envio da fatura por email. Email pré-preenchido com
 * o destinatário sugerido (cliente.billingEmail ?? email) e editável pelo
 * operador. O PDF é anexado automaticamente pelo backend. Funciona tanto para
 * faturas per-dossier quanto consolidadas (resolver compartilhado no backend).
 */
export function SendEmailModal({
  companyId,
  invoice,
  batch,
  onClose,
  onSent,
}: {
  companyId: number;
  invoice: EmailInvoice;
  /** F-relance-lote — várias faturas (uma relance por cliente, todos os PDFs). */
  batch?: EmailInvoice[];
  onClose: () => void;
  /** Envio concluído (lote ou uma fatura); `sentTo` = destinatário da fatura única. */
  onSent?: (sentTo?: string) => void;
}) {
  const t = useT();
  const isBatch = (batch?.length ?? 0) > 1;
  const isReminder = invoice.mode === "reminder" || isBatch;
  const lote = batch ?? [invoice];
  const clientes = [...new Set(lote.map((i) => i.customerName ?? ""))];
  const umCliente = clientes.length <= 1;
  const modals = useModals();
  const utils = trpc.useUtils();
  const suggestedQuery = trpc.customerInvoices.getSuggestedRecipientEmail.useQuery({
    companyId,
    invoiceId: invoice.id,
  });
  const [email, setEmail] = useState("");
  // F-invoice-email-message — texto livre opcional no corpo do e-mail.
  // F-relance-texto (CEO 2026-10-12): relance vem pré-preenchida (editável).
  const [message, setMessage] = useState(() => (isReminder ? textoRelanceFr(lote) : ""));
  const [edited, setEdited] = useState(false);
  // F-releve-conta (CEO 2026-11-03) — relance com o relevé de compte anexo.
  const [anexarReleve, setAnexarReleve] = useState(true);

  // Pré-preenche com o sugerido até o operador editar (não sobrescreve edição manual).
  useEffect(() => {
    if (!edited && suggestedQuery.data?.suggested) {
      setEmail(suggestedQuery.data.suggested);
    }
  }, [suggestedQuery.data?.suggested, edited]);

  const sendMut = trpc.customerInvoices.sendEmail.useMutation({
    onSuccess: (res) => {
      void utils.customerInvoices.list.invalidate();
      // CEO 2026-11-01 — a fatura sai do alerta "não enviadas".
      void utils.customerInvoices.unsentSummary.invalidate();
      onSent?.(res.sentTo);
      void modals.alert({
        title: t("invoices.emailSentTitle"),
        message: `${t("invoices.emailSentPre")}${invoice.invoiceNumber}${t("invoices.emailSentMid")}${res.sentTo}${t("invoices.emailSentSuf")}`,
      });
      onClose();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.emailFailedTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const batchMut = trpc.customerInvoices.sendReminderBatch.useMutation({
    onSuccess: (res) => {
      void utils.customerInvoices.list.invalidate();
      void utils.customerInvoices.unsentSummary.invalidate();
      const linhas = res.sent.map(
        (g) => `${g.customerName} → ${g.sentTo}: ${g.invoiceNumbers.join(", ")}`,
      );
      if (res.withoutEmail.length > 0)
        linhas.push(`${t("invoices.noCustomerEmailMsg")} ${res.withoutEmail.join(", ")}`);
      void modals.alert({
        title: t("invoices.reminderBatchSentTitle"),
        message: linhas.join("\n"),
      });
      onSent?.();
      onClose();
    },
    onError: (e) =>
      void modals.alert({
        title: t("invoices.emailFailedTitle"),
        message: e.message,
        tone: "danger",
      }),
  });

  const trimmed = email.trim();
  const valid = isBatch && !umCliente ? true : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold">
          {isBatch
            ? `${t("invoices.reminderBatchTitlePre")} ${lote.length} ${t("invoices.reminderBatchTitleSuf")}`
            : isReminder
              ? t("invoices.reminderModalTitle")
              : t("invoices.emailModalTitle")}
        </h2>
        {isReminder && (
          <p className="mt-1 text-xs" style={{ color: "#DC2626" }}>
            {t("invoices.reminderModalHint")}
          </p>
        )}
        <p className="mt-1 text-xs text-neutral-500">
          {t("invoices.emailModalInvoicePre")}{" "}
          <span className="font-mono text-lime">{lote.map((i) => i.invoiceNumber).join(", ")}</span>
          {isBatch
            ? umCliente
              ? ` — ${clientes[0]}`
              : ` — ${clientes.length} ${t("invoices.customersWord")}`
            : suggestedQuery.data?.customerName
              ? ` — ${suggestedQuery.data.customerName}`
              : ""}
        </p>

        {isBatch && !umCliente ? (
          <p className="mt-4 rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-xs text-neutral-300">
            {t("invoices.reminderBatchMultiCustomers")}
          </p>
        ) : (
          <label className="mt-4 block text-sm text-neutral-300">
            {t("invoices.recipientEmailLabel")}
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEdited(true);
              }}
              placeholder={t("invoices.recipientEmailPlaceholder")}
              className="mt-1 w-full rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
              autoFocus
            />
          </label>
        )}
        {/* CEO 2026-11-01 — contatos do cliente com e-mail: um clique preenche. */}
        {!(isBatch && !umCliente) && (suggestedQuery.data?.contacts?.length ?? 0) > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="send-contacts">
            {suggestedQuery.data!.contacts.map((c) => (
              <button
                key={c.email}
                type="button"
                onClick={() => {
                  setEmail(c.email);
                  setEdited(true);
                }}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] ${
                  email.trim().toLowerCase() === c.email.toLowerCase()
                    ? "border-lime bg-lime/15"
                    : "border-neutral-700 hover:border-lime"
                }`}
                title={c.email}
                data-testid={`send-contact-${c.email}`}
              >
                {c.receivesInvoices ? "✉ " : ""}
                {c.name}
                {c.role ? ` · ${c.role}` : ""}
              </button>
            ))}
          </div>
        )}
        {!(isBatch && !umCliente) && suggestedQuery.data && !suggestedQuery.data.suggested && (
          <p className="mt-1 text-xs text-amber-400">{t("invoices.noCustomerEmailMsg")}</p>
        )}
        <p className="mt-1 text-xs text-neutral-500">{t("invoices.pdfAutoAttachedMsg")}</p>
        {isReminder && (
          <label className="mt-2 flex items-center gap-2 text-sm text-neutral-300">
            <input
              type="checkbox"
              checked={anexarReleve}
              onChange={(e) => setAnexarReleve(e.target.checked)}
              className="accent-lime"
              data-testid="reminder-attach-statement"
            />
            {t("invoices.attachStatement")}
          </label>
        )}
        <label className="mt-3 block text-sm text-neutral-300">
          {t("invoices.emailMessageLabel")}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={4000}
            rows={isReminder ? 9 : 4}
            placeholder={t("invoices.emailMessagePh")}
            className="mt-1 w-full resize-y rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm outline-none focus:border-lime"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm text-neutral-300 hover:bg-neutral-800"
          >
            {t("invoices.cancel")}
          </button>
          <button
            onClick={() =>
              isBatch
                ? batchMut.mutate({
                    companyId,
                    invoiceIds: lote.map((i) => i.id),
                    ...(umCliente && trimmed ? { recipientEmail: trimmed } : {}),
                    customMessage: message.trim() || undefined,
                    attachStatement: anexarReleve,
                  })
                : sendMut.mutate({
                    companyId,
                    invoiceId: invoice.id,
                    recipientEmail: trimmed,
                    mode: isReminder ? "reminder" : "invoice",
                    customMessage: message.trim() || undefined,
                    ...(isReminder ? { attachStatement: anexarReleve } : {}),
                  })
            }
            disabled={!valid || sendMut.isPending || batchMut.isPending}
            className="rounded-md bg-lime px-4 py-1.5 text-sm font-medium text-on-lime hover:opacity-90 disabled:opacity-40"
          >
            {sendMut.isPending || batchMut.isPending
              ? t("invoices.sending")
              : isReminder
                ? t("invoices.sendReminder")
                : t("invoices.send")}
          </button>
        </div>
      </div>
    </div>
  );
}
